// Deployment configuration for the partner shell.
//
// Every value here is an origin, a key, or a claim parameter that is owned
// outside this repo. Each is overridable at build time with a VITE_ variable
// and carries a default that is only a default. Items marked UNCONFIRMED are
// the ones this lead cannot pin from a source it is allowed to read; they are
// tracked in ESCALATIONS.md. Nothing here is invented as fact.

const env = import.meta.env

/** Public site origin. The For Business door lives here, owned by youid-web. */
export const WEBSITE_ORIGIN: string =
  env.VITE_WEBSITE_ORIGIN ?? 'https://whoyouare.ai'

/**
 * UNCONFIRMED (spec section 7, open question 1; also B7). The exact For
 * Business login URL and the name of its return parameter are web-owned and
 * not yet given. These defaults are placeholders. Because the session verifier
 * is not_configured until a key is provisioned, no automatic navigation to
 * this URL happens yet, so a placeholder here is inert rather than wrong.
 */
export const FOR_BUSINESS_URL: string =
  env.VITE_FOR_BUSINESS_URL ?? `${WEBSITE_ORIGIN}/for-business`
export const RETURN_TO_PARAM: string = env.VITE_RETURN_TO_PARAM ?? 'return_to'

/**
 * The session verifier's public key, PEM or base64 by build config. Verify
 * only: this app holds a PUBLIC key and can never mint a token (spec 3.1).
 * Absent key is the live state today and resolves to not_configured, which is
 * the state the app shows until the founder provisions it. Do not work around
 * this by faking a session.
 */
export const SESSION_PUBLIC_KEY: string | undefined = env.VITE_SESSION_PUBLIC_KEY

/** Session claim version (`sv`). v2 is Ed25519 over JCS claims (spec 3.1). */
export const SESSION_VERSION: number = Number(env.VITE_SESSION_VERSION ?? '2')

/**
 * UNCONFIRMED (B7). The `.whoyouare.ai` cookie name is defined in
 * youid-backend SESSION-CONTRACT section 10, which is another repo this lead
 * may not read (LEAD.md section 1). Placeholder until supplied; unused while
 * not_configured, since nothing is read or verified in that state.
 */
export const SESSION_COOKIE: string = env.VITE_SESSION_COOKIE ?? 'youid_session'

/**
 * Partner server origin that the /v1 HTTP client targets. The browser never
 * calls /v1 directly and never holds CRED (V1-CONTRACT section 5); the partner
 * server verifies the web session and proxies to /v1 with CRED or a
 * server-held DID session. Undefined selects the fixture client (the default
 * today, no backend run-shell). Setting it selects the HTTP client, which is
 * the entire swap: no call site changes.
 */
export const PARTNER_SERVER_BASE: string | undefined = env.VITE_PARTNER_SERVER_BASE

/**
 * Explicit client selector. `fixture` forces the sandbox client in any build
 * (local demos, CI); unset defers to the environment. Fixtures otherwise serve
 * only in a dev build; a production build with no partner-server base fails
 * closed to not_configured rather than serving sandbox data to a real partner.
 */
export const V1_CLIENT_MODE: string | undefined = env.VITE_V1_CLIENT
